/**
 * SwapEntryForm - CRUD for XRP->COREUM swap entries
 */

import React, { useState } from 'react';
import { Button } from '../../ui/components/Button';
import { TextField } from '../../ui/components/TextField';
import { FeatherPlus, FeatherTrash2 } from '@subframe/core';
import {
  useSwapEntries,
  useCreateSwapEntry,
  useDeleteSwapEntry,
} from '../../hooks/useDistribution';

interface SwapEntryFormProps {
  projectSlug: string;
}

export const SwapEntryForm: React.FC<SwapEntryFormProps> = ({ projectSlug }) => {
  const { data, isLoading } = useSwapEntries(projectSlug);
  const createMutation = useCreateSwapEntry(projectSlug);
  const deleteMutation = useDeleteSwapEntry(projectSlug);

  const [showForm, setShowForm] = useState(false);
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [fromAmount, setFromAmount] = useState('');
  const [fromCurrency, setFromCurrency] = useState('XRP');
  const [toAmount, setToAmount] = useState('');
  const [toCurrency, setToCurrency] = useState('COREUM');
  const [notes, setNotes] = useState('');

  const entries = data?.entries || [];

  const resetForm = () => {
    setEntryDate(new Date().toISOString().slice(0, 10));
    setFromAmount('');
    setToAmount('');
    setNotes('');
    setShowForm(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fromAmount || !toAmount) return;

    const from = parseFloat(fromAmount);
    const to = parseFloat(toAmount);
    createMutation.mutate(
      {
        entry_date: entryDate,
        from_amount: from,
        from_currency: fromCurrency,
        to_amount: to,
        to_currency: toCurrency,
        rate: from > 0 ? to / from : undefined,
        notes: notes || undefined,
      },
      { onSuccess: resetForm }
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <span className="text-body-bold font-body-bold text-default-font">Swap Entries</span>
        {!showForm && (
          <Button variant="brand-secondary" size="small" icon={<FeatherPlus />} onClick={() => setShowForm(true)}>
            Add Swap
          </Button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 rounded border border-solid border-neutral-border p-4">
          <TextField className="h-auto w-full max-w-[200px]" label="Date">
            <TextField.Input type="date" value={entryDate} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEntryDate(e.target.value)} />
          </TextField>
          <div className="flex gap-3 items-end">
            <TextField className="h-auto flex-1" label={`From (${fromCurrency})`}>
              <TextField.Input type="number" step="any" min="0" value={fromAmount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFromAmount(e.target.value)} />
            </TextField>
            <span className="text-subtext-color pb-2">&rarr;</span>
            <TextField className="h-auto flex-1" label={`To (${toCurrency})`}>
              <TextField.Input type="number" step="any" min="0" value={toAmount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setToAmount(e.target.value)} />
            </TextField>
          </div>
          <TextField className="h-auto w-full" label="Notes (optional)">
            <TextField.Input value={notes} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotes(e.target.value)} />
          </TextField>
          {createMutation.error && (
            <span className="text-caption font-caption text-red-600">{createMutation.error.message}</span>
          )}
          <div className="flex gap-2">
            <Button type="submit" variant="brand-primary" size="small" disabled={createMutation.isPending}>Add</Button>
            <Button variant="neutral-secondary" size="small" onClick={resetForm}>Cancel</Button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="text-subtext-color py-4">Loading swaps...</div>
      ) : entries.length === 0 ? (
        <div className="text-subtext-color py-4">No swap entries yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-body font-body">
            <thead>
              <tr className="border-b border-neutral-border text-left text-caption font-caption text-subtext-color">
                <th className="px-2 py-1">Date</th>
                <th className="px-2 py-1 text-right">From</th>
                <th className="px-2 py-1 text-right">To</th>
                <th className="px-2 py-1 text-right">Rate</th>
                <th className="px-2 py-1">Notes</th>
                <th className="px-2 py-1 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr key={entry.id} className="border-b border-neutral-border">
                  <td className="px-2 py-1 text-default-font">{new Date(entry.entry_date).toLocaleDateString()}</td>
                  <td className="px-2 py-1 text-right text-default-font">
                    {Number(entry.from_amount).toLocaleString()} {entry.from_currency}
                  </td>
                  <td className="px-2 py-1 text-right text-default-font">
                    {Number(entry.to_amount).toLocaleString()} {entry.to_currency}
                  </td>
                  <td className="px-2 py-1 text-right text-subtext-color">
                    {entry.rate ? Number(entry.rate).toFixed(4) : '-'}
                  </td>
                  <td className="px-2 py-1 text-subtext-color">{entry.notes || '-'}</td>
                  <td className="px-2 py-1">
                    <button onClick={() => deleteMutation.mutate(entry.id)} className="p-1 rounded hover:bg-red-50">
                      <FeatherTrash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
